import React, { useState, useCallback } from 'react';
import { theme } from '../../styles/theme';
import { RunState } from '../../types/run';
import { Card, CardCategory } from '../../types/game';
import {
  bankMarks,
  sellItems,
  getPackCost,
  getPackOptions,
  getDraftOptionCount,
  buyPackCard,
  payPackCost,
  buyDirectCard,
  removeCard,
  PackType,
} from '../../engine/runState';
import { tuning } from '../../data/tuning';
import { CARD_TEMPLATES } from '../../data/cards';

const CATEGORY_COLORS: Record<CardCategory, string> = {
  movement: '#66ccff',
  intel: '#aa88ff',
  tactics: '#ffaa44',
  combat: '#ff5555',
  fieldcraft: '#44cc88',
  junk: '#777777',
};

const PACK_INFO: Record<PackType, { label: string; color: string }> = {
  intel: { label: 'INTEL', color: '#aa88ff' },
  tactics: { label: 'TACTICS', color: '#ffaa44' },
  fieldcraft: { label: 'FIELDCRAFT', color: '#44cc88' },
};

interface HQScreenProps {
  runState: RunState;
  onDeploy: (updatedRun: RunState) => void;
  onEndRun: () => void;
  shopEnabled: boolean;
}

type ShopView = 'main' | 'pack-intel' | 'pack-tactics' | 'pack-fieldcraft';

export function HQScreen({ runState: initialRun, onDeploy, onEndRun, shopEnabled }: HQScreenProps) {
  // Apply automatic HQ actions on mount: bank marks, sell items.
  // Ship 1: drop-card recovery removed (no forced drops anymore).
  const [run, setRun] = useState<RunState>(() => {
    let r = initialRun;
    const sold = sellItems(r);
    r = sold.run;
    r = bankMarks(r);
    return r;
  });

  const [itemsSoldTotal] = useState(() =>
    initialRun.collectedItems.reduce((sum, item) => sum + (item.sellValue ?? 10), 0),
  );

  // Shop state
  const [shopView, setShopView] = useState<ShopView>('main');
  const [packOptions, setPackOptions] = useState<Card[] | null>(null);
  const [removingCards, setRemovingCards] = useState(false);
  const [packUsed, setPackUsed] = useState(false); // one pack open per HQ visit
  const [confirmEndRun, setConfirmEndRun] = useState(false);

  const removalCost = tuning.marks.shopRemovalCost;
  const canAffordRemoval = run.marks >= removalCost;
  const canRemove = run.deck.length > tuning.marks.minDeckSize;

  // Current pack type derived from shopView
  const currentPackType: PackType | null = shopView.startsWith('pack-')
    ? shopView.replace('pack-', '') as PackType : null;
  const currentPackCost = currentPackType ? getPackCost(run, currentPackType) : 0;
  const canAffordPack = currentPackType ? run.marks >= currentPackCost : false;

  // Direct buy state
  const moveCost = tuning.marks.shopDirectMoveCost;
  const shortcutCost = tuning.marks.shopDirectShortcutCost;
  const canBuyMove = !run.directBuyUsed && run.marks >= moveCost;
  const canBuyShortcut = !run.directBuyUsed && run.marks >= shortcutCost;

  // ── Pack flow handlers ──

  const handleOpenPack = useCallback(() => {
    if (!currentPackType) return;
    // Severance passive widens the pack from 3 → 5 options (repurposed from
    // its old drop-card role now that forced drops are gone).
    const options = getPackOptions(currentPackType, getDraftOptionCount(run));
    setPackOptions(options);
  }, [currentPackType, run]);

  const handlePickCard = useCallback((card: Card) => {
    if (!currentPackType) return;
    setRun(prev => buyPackCard(prev, card, currentPackType));
    setPackOptions(null);
    setPackUsed(true);
    setShopView('main');
  }, [currentPackType]);

  const handleDeclinePack = useCallback(() => {
    if (!currentPackType) return;
    // Pay cost even if declined (they opened the pack)
    setRun(prev => payPackCost(prev, currentPackType));
    setPackOptions(null);
    setPackUsed(true);
    setShopView('main');
  }, [currentPackType]);

  const handleBackFromPack = useCallback(() => {
    // Free to back out if pack hasn't been opened yet
    if (packOptions) return; // can't back out once opened — must pick or decline
    setShopView('main');
  }, [packOptions]);

  // ── Direct buy handlers ──

  const handleDirectBuy = useCallback((templateKey: 'move' | 'shortcut') => {
    setRun(prev => buyDirectCard(prev, templateKey));
  }, []);

  // ── Card removal ──

  const handleRemoveCard = useCallback((cardId: string) => {
    setRun(prev => removeCard(prev, cardId));
  }, []);

  // ── Navigation ──

  const handleDeploy = useCallback(() => { onDeploy(run); }, [run, onDeploy]);
  const handleEndRun = useCallback(() => {
    if (!confirmEndRun) { setConfirmEndRun(true); return; }
    onEndRun();
  }, [confirmEndRun, onEndRun]);

  return (
    <div style={styles.container}>
      <div style={styles.page}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerClassification}>TOP SECRET</div>
          <div style={styles.headerTitle}>HEADQUARTERS</div>
          <div style={styles.headerRound}>ROUND {run.round} DEBRIEF</div>
        </div>

        <div style={styles.content}>
          {/* ═══ Left panel — status ═══ */}
          <div style={styles.leftPanel}>
            <div style={styles.section}>
              <div style={styles.sectionTitle}>MARKS</div>
              <div style={styles.markDisplay}>
                <span style={styles.markValue}>{run.marks}</span>
                <span style={styles.markLabel}> BANKED</span>
              </div>
              {itemsSoldTotal > 0 && (
                <div style={styles.infoLine}>Items sold: +{itemsSoldTotal} marks</div>
              )}
            </div>

            <div style={styles.section}>
              <div style={styles.sectionTitle}>DECK ({run.deck.length} cards)</div>
              <div style={styles.deckSummary}>
                {(['movement', 'intel', 'tactics', 'combat', 'fieldcraft'] as CardCategory[]).map(cat => {
                  const count = run.deck.filter(c => c.category === cat).length;
                  if (count === 0) return null;
                  return (
                    <span key={cat} style={{ ...styles.deckCat, color: CATEGORY_COLORS[cat] }}>
                      {cat.toUpperCase()} {count}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ═══ Right panel — shop ═══ */}
          {shopEnabled && (
            <div style={styles.rightPanel}>
              {/* ── Main shop view ── */}
              {shopView === 'main' && (
                <>
                  {/* Card packs */}
                  <div style={styles.section}>
                    <div style={styles.sectionTitle}>CARD PACKS {packUsed && <span style={{ color: theme.colors.textDim, fontWeight: 400 }}>— OPENED</span>}</div>
                    <div style={styles.packRow}>
                      {(['intel', 'tactics', 'fieldcraft'] as PackType[]).map(pt => {
                        const info = PACK_INFO[pt];
                        const cost = getPackCost(run, pt);
                        const canOpen = !packUsed && run.marks >= cost;
                        return (
                          <button
                            key={pt}
                            style={{
                              ...styles.packButton,
                              borderColor: canOpen ? info.color + '60' : theme.colors.border,
                              opacity: canOpen ? 1 : 0.4,
                              cursor: canOpen ? 'pointer' : 'default',
                            }}
                            onClick={canOpen ? () => setShopView(`pack-${pt}` as ShopView) : undefined}
                            onMouseEnter={canOpen ? e => { (e.currentTarget as HTMLElement).style.borderColor = info.color; } : undefined}
                            onMouseLeave={canOpen ? e => { (e.currentTarget as HTMLElement).style.borderColor = info.color + '60'; } : undefined}
                          >
                            <div style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '2px', color: canOpen ? info.color : theme.colors.textDim }}>{info.label}</div>
                            <div style={{ fontSize: '10px', color: theme.colors.textDim, marginTop: '4px' }}>{packUsed ? 'USED' : `${cost} MK`}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Direct buys — styled as cards */}
                  <div style={styles.section}>
                    <div style={styles.sectionTitle}>DIRECT BUY {run.directBuyUsed && <span style={{ color: theme.colors.textDim, fontWeight: 400 }}>— USED</span>}</div>
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                      {([
                        { key: 'move' as const, cost: moveCost, canBuy: canBuyMove },
                        { key: 'shortcut' as const, cost: shortcutCost, canBuy: canBuyShortcut },
                      ]).map(({ key, cost, canBuy }) => {
                        const tpl = CARD_TEMPLATES[key];
                        const catColor = CATEGORY_COLORS[tpl.category];
                        return (
                          <div
                            key={key}
                            style={{
                              ...styles.cardTile,
                              opacity: canBuy ? 1 : 0.35,
                              cursor: canBuy ? 'pointer' : 'default',
                              borderColor: canBuy ? catColor + '40' : theme.colors.border,
                            }}
                            onClick={canBuy ? () => handleDirectBuy(key) : undefined}
                          >
                            <div style={styles.cardTileCost}>{tpl.apCost}</div>
                            <div style={styles.cardTileName}>{tpl.name}</div>
                            <div style={{ ...styles.cardTileCat, color: catColor }}>{tpl.category.toUpperCase()}</div>
                            {tpl.movementRange && (
                              <div style={{ ...styles.cardTileRange, color: catColor }}>{tpl.movementRange}m</div>
                            )}
                            <div style={styles.cardTilePrice}>{cost} MK</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Card removal */}
                  <div style={styles.section}>
                    <div style={styles.sectionTitle}>CARD REMOVAL</div>
                    {!removingCards ? (
                      <button
                        style={{
                          ...styles.shopButton,
                          opacity: canAffordRemoval && canRemove ? 1 : 0.4,
                          cursor: canAffordRemoval && canRemove ? 'pointer' : 'default',
                        }}
                        onClick={canAffordRemoval && canRemove ? () => setRemovingCards(true) : undefined}
                      >
                        REMOVE CARD — {removalCost} MARKS
                      </button>
                    ) : (
                      <div style={styles.removalPanel}>
                        <div style={styles.draftLabel}>Select a card to permanently remove:</div>
                        <div style={styles.removalList}>
                          {run.deck.map(card => {
                            const catColor = CATEGORY_COLORS[card.category];
                            return (
                              <button
                                key={card.id}
                                style={styles.removalRow}
                                onClick={() => { handleRemoveCard(card.id); setRemovingCards(false); }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,80,80,0.08)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'none'; }}
                              >
                                <span style={{ ...styles.cardDot, background: catColor }} />
                                <span style={styles.removalName}>{card.name}</span>
                                <span style={styles.removalCat}>{card.category}</span>
                                <span style={styles.removalAp}>{card.apCost} AP</span>
                              </button>
                            );
                          })}
                        </div>
                        <button style={styles.cancelButton} onClick={() => setRemovingCards(false)}>CANCEL</button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Pack view renders as full-screen overlay below */}
            </div>
          )}

          {/* Death message when shop disabled */}
          {!shopEnabled && (
            <div style={styles.rightPanel}>
              <div style={styles.section}>
                <div style={{ ...styles.sectionTitle, color: '#ff5050' }}>AGENT DOWN</div>
                <div style={styles.deathMsg}>
                  You were killed in action. No shop access this round.
                  Unbanked marks have been penalized. Deploy to try again.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bottom actions */}
        <div style={styles.bottomBar}>
          <button style={styles.endRunButton} onClick={handleEndRun}>
            {confirmEndRun ? 'CONFIRM END RUN?' : 'END RUN'}
          </button>
          <button
            style={styles.deployButton}
            onClick={handleDeploy}
            onMouseEnter={e => { (e.target as HTMLElement).style.background = 'rgba(68, 204, 136, 0.2)'; }}
            onMouseLeave={e => { (e.target as HTMLElement).style.background = 'rgba(68, 204, 136, 0.08)'; }}
          >
            RETURN TO RUN PATH
          </button>
        </div>
      </div>

      {/* ═══ Full-screen pack overlay ═══ */}
      {currentPackType && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 100,
          background: 'rgba(8, 12, 20, 0.98)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          fontFamily: theme.fonts.mono,
        }}>
          {/* Exit button */}
          {!packOptions && (
            <button
              onClick={handleBackFromPack}
              style={{
                position: 'absolute', top: '24px', left: '24px',
                background: 'none', border: `1px solid ${theme.colors.border}`,
                color: theme.colors.textSecondary, fontFamily: theme.fonts.mono,
                fontSize: '10px', letterSpacing: '1px', padding: '6px 14px',
                cursor: 'pointer', borderRadius: '3px',
              }}
            >
              ← BACK TO HQ
            </button>
          )}

          {/* Marks display */}
          <div style={{ position: 'absolute', top: '24px', right: '24px', fontSize: '12px', color: '#44cc88' }}>
            <span style={{ fontWeight: 700, fontSize: '16px' }}>{run.marks}</span>
            <span style={{ letterSpacing: '2px', color: theme.colors.textSecondary, marginLeft: '6px', fontSize: '10px' }}>MARKS</span>
          </div>

          {/* Pack title */}
          <div style={{
            fontSize: '14px', fontWeight: 700, letterSpacing: '4px',
            color: PACK_INFO[currentPackType].color, marginBottom: '32px',
          }}>
            {PACK_INFO[currentPackType].label} PACK
          </div>

          {!packOptions ? (
            /* Draw button — circular, shows cost */
            <div style={{ textAlign: 'center' }}>
              <button
                style={{
                  width: '120px', height: '120px', borderRadius: '50%',
                  background: canAffordPack ? `${PACK_INFO[currentPackType].color}15` : 'rgba(255,255,255,0.02)',
                  border: `2px solid ${canAffordPack ? PACK_INFO[currentPackType].color : theme.colors.border}`,
                  color: canAffordPack ? PACK_INFO[currentPackType].color : theme.colors.textDim,
                  fontFamily: theme.fonts.mono, fontWeight: 700,
                  cursor: canAffordPack ? 'pointer' : 'default',
                  opacity: canAffordPack ? 1 : 0.4,
                  transition: 'all 0.2s',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto', padding: 0,
                }}
                onClick={canAffordPack ? handleOpenPack : undefined}
              >
                <div style={{ fontSize: '24px' }}>{currentPackCost}</div>
                <div style={{ fontSize: '9px', letterSpacing: '2px', marginTop: '4px' }}>MARKS</div>
              </button>
              <div style={{ fontSize: '12px', color: theme.colors.textPrimary, marginTop: '20px' }}>
                Press to open — cost is paid whether you pick or not
              </div>
            </div>
          ) : (
            /* Revealed cards — styled as proper cards at ~2× the in-hand size.
               Cards are the primary focus of this screen, so they're big,
               readable, and show the full description + hover glow. */
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginBottom: '28px' }}>
                Pick one card to add to your deck:
              </div>
              <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', marginBottom: '28px', flexWrap: 'wrap' }}>
                {packOptions.map(card => {
                  const catColor = CATEGORY_COLORS[card.category];
                  return (
                    <div
                      key={card.id}
                      style={{
                        width: '220px', height: '300px',
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        gap: '6px', position: 'relative',
                        background: 'rgba(12, 16, 24, 0.92)',
                        border: `1px solid ${catColor}40`,
                        borderTop: `4px solid ${catColor}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontFamily: theme.fonts.mono,
                        transition: 'all 0.15s',
                        padding: '24px 16px 16px',
                      }}
                      onClick={() => handlePickCard(card)}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = catColor; (e.currentTarget as HTMLElement).style.boxShadow = `0 0 24px ${catColor}40`; (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = catColor + '40'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; (e.currentTarget as HTMLElement).style.transform = 'none'; }}
                    >
                      {/* Big AP cost badge top-left */}
                      <div style={{
                        position: 'absolute', top: '10px', left: '12px',
                        fontSize: '26px', fontWeight: 700, color: theme.colors.accentYellow,
                      }}>{card.apCost}</div>
                      {/* Card name */}
                      <div style={{
                        fontSize: '18px', fontWeight: 700, color: theme.colors.textPrimary,
                        letterSpacing: '1px', textAlign: 'center', marginTop: '10px',
                      }}>{card.name}</div>
                      {/* Category tag */}
                      <div style={{
                        fontSize: '10px', letterSpacing: '2.5px', color: catColor, fontWeight: 700,
                      }}>{card.category.toUpperCase()}</div>
                      {/* Movement range if applicable */}
                      {card.movementRange && (
                        <div style={{
                          fontSize: '12px', letterSpacing: '1px', color: catColor, fontWeight: 600, marginTop: '2px',
                        }}>{card.movementRange}m range</div>
                      )}
                      {/* Full description */}
                      <div style={{
                        fontSize: '11px', color: theme.colors.textSecondary,
                        lineHeight: '1.55', textAlign: 'center',
                        padding: '8px 4px 0', flex: 1,
                      }}>
                        {card.description}
                      </div>
                      {/* Condition — if any */}
                      {card.condition && (
                        <div style={{
                          fontSize: '9px', color: '#ff8844', lineHeight: '1.4',
                          textAlign: 'center', padding: '0 4px', letterSpacing: '0.5px',
                          marginTop: '4px',
                        }}>{card.condition}</div>
                      )}
                      {/* Keyword badges (Burn / Chain / Extract) */}
                      {(card.isBurn || card.isChain || card.isExtractRequired) && (
                        <div style={{ display: 'flex', gap: '4px', marginTop: '6px' }}>
                          {card.isBurn && <div style={{ fontSize: '8px', fontWeight: 700, letterSpacing: '1px', color: '#ff5032', background: 'rgba(255, 80, 50, 0.15)', padding: '2px 6px', borderRadius: '2px' }}>BURN</div>}
                          {card.isChain && <div style={{ fontSize: '8px', fontWeight: 700, letterSpacing: '1px', color: '#44aaff', background: 'rgba(68, 170, 255, 0.15)', padding: '2px 6px', borderRadius: '2px' }}>CHAIN</div>}
                          {card.isExtractRequired && <div style={{ fontSize: '8px', fontWeight: 700, letterSpacing: '1px', color: '#ffcc44', background: 'rgba(255, 204, 68, 0.15)', padding: '2px 6px', borderRadius: '2px' }}>EXTRACT</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <button
                style={{
                  background: 'none', border: `1px solid ${theme.colors.border}`,
                  color: theme.colors.textSecondary, padding: '8px 20px', fontSize: '10px',
                  letterSpacing: '2px', cursor: 'pointer', fontFamily: theme.fonts.mono, borderRadius: '4px',
                }}
                onClick={handleDeclinePack}
              >
                NO THANKS
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed', inset: 0,
    background: 'rgba(8, 12, 20, 0.98)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: theme.fonts.mono, overflow: 'auto',
  },
  page: {
    width: '100%', maxWidth: '900px', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column', overflow: 'auto', padding: '32px 24px',
  },
  header: { textAlign: 'center', marginBottom: '32px' },
  headerClassification: { fontSize: '9px', letterSpacing: '4px', color: '#ff5050', fontWeight: 700, marginBottom: '8px' },
  headerTitle: { fontSize: '28px', fontWeight: 700, letterSpacing: '8px', color: theme.colors.textPrimary },
  headerRound: { fontSize: '11px', letterSpacing: '3px', color: theme.colors.textSecondary, marginTop: '8px' },
  content: { display: 'flex', gap: '32px', flex: 1, minHeight: 0 },
  leftPanel: { flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' },
  rightPanel: { flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' },
  section: {
    background: 'rgba(255,255,255,0.02)', border: `1px solid ${theme.colors.border}`,
    borderRadius: '6px', padding: '16px',
  },
  sectionTitle: {
    fontSize: '10px', letterSpacing: '3px', fontWeight: 700,
    color: theme.colors.textSecondary, marginBottom: '12px',
  },
  markDisplay: { fontSize: '18px', color: '#44cc88' },
  markValue: { fontWeight: 700, fontSize: '28px' },
  markLabel: { fontSize: '12px', letterSpacing: '2px', color: theme.colors.textSecondary },
  infoLine: { fontSize: '11px', color: theme.colors.textSecondary, marginTop: '8px' },
  cardList: { display: 'flex', flexDirection: 'column', gap: '4px' },
  cardRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' },
  cardDot: { width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0 },
  cardRowName: { fontSize: '11px', color: theme.colors.textPrimary, flex: 1 },
  cardRowCost: { fontSize: '10px', color: theme.colors.textDim },
  deckSummary: { display: 'flex', flexWrap: 'wrap', gap: '12px' },
  deckCat: { fontSize: '10px', letterSpacing: '1px', fontWeight: 600 },

  // Pack row — 3 pack type buttons
  packRow: { display: 'flex', gap: '8px' },
  packButton: {
    flex: 1, background: 'rgba(255,255,255,0.02)',
    border: `1px solid ${theme.colors.border}`, borderRadius: '4px',
    padding: '12px 8px', textAlign: 'center', cursor: 'pointer',
    fontFamily: theme.fonts.mono, transition: 'all 0.15s',
  },

  // Direct buy row
  directRow: { display: 'flex', gap: '8px' },
  directButton: {
    flex: 1, background: 'rgba(255,255,255,0.02)',
    border: `1px solid ${theme.colors.border}`, borderRadius: '4px',
    padding: '10px 8px', textAlign: 'center', cursor: 'pointer',
    fontFamily: theme.fonts.mono, transition: 'all 0.15s',
  },

  // Shared
  shopButton: {
    background: 'rgba(255,255,255,0.04)', border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textPrimary, padding: '12px 16px', fontSize: '11px',
    fontWeight: 700, letterSpacing: '2px', fontFamily: theme.fonts.mono,
    borderRadius: '4px', width: '100%', transition: 'all 0.15s',
  },
  backButton: {
    background: 'none', border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textSecondary, fontFamily: theme.fonts.mono,
    fontSize: '10px', letterSpacing: '1px', padding: '4px 10px',
    cursor: 'pointer', borderRadius: '2px',
  },
  draftLabel: { fontSize: '11px', color: theme.colors.textSecondary, marginBottom: '8px' },
  draftGrid: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' },
  draftCard: {
    background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '4px', padding: '12px', cursor: 'pointer',
    fontFamily: theme.fonts.mono, textAlign: 'left', transition: 'all 0.15s',
  },
  draftCardCat: { fontSize: '8px', letterSpacing: '2px', fontWeight: 700, marginBottom: '4px' },
  draftCardName: { fontSize: '12px', fontWeight: 700, color: theme.colors.textPrimary, marginBottom: '2px' },
  draftCardCost: { fontSize: '9px', color: theme.colors.textSecondary, marginBottom: '6px' },
  draftCardDesc: { fontSize: '9px', color: theme.colors.textSecondary, lineHeight: '1.4' },
  cancelButton: {
    background: 'none', border: `1px solid ${theme.colors.border}`,
    color: theme.colors.textSecondary, padding: '8px 16px', fontSize: '10px',
    letterSpacing: '2px', cursor: 'pointer', fontFamily: theme.fonts.mono,
    borderRadius: '4px', alignSelf: 'flex-start',
  },
  removalPanel: { display: 'flex', flexDirection: 'column', gap: '8px' },
  removalList: { display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '300px', overflowY: 'auto' },
  removalRow: {
    display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px',
    background: 'none', border: 'none', cursor: 'pointer',
    fontFamily: theme.fonts.mono, borderRadius: '3px', transition: 'background 0.1s',
  },
  removalName: { fontSize: '11px', color: theme.colors.textPrimary, flex: 1, textAlign: 'left' },
  removalCat: { fontSize: '9px', color: theme.colors.textDim, letterSpacing: '1px' },
  removalAp: { fontSize: '9px', color: theme.colors.textDim },
  deathMsg: { fontSize: '12px', color: theme.colors.textSecondary, lineHeight: '1.7' },
  bottomBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginTop: '32px', paddingTop: '20px', borderTop: `1px solid ${theme.colors.border}`,
  },
  endRunButton: {
    background: 'none', border: '1px solid rgba(255, 80, 80, 0.3)', color: '#ff5050',
    padding: '10px 20px', fontSize: '11px', fontWeight: 600, letterSpacing: '2px',
    cursor: 'pointer', fontFamily: theme.fonts.mono, borderRadius: '4px',
  },
  deployButton: {
    background: 'rgba(68, 204, 136, 0.08)', border: '1px solid rgba(68, 204, 136, 0.5)',
    color: '#44cc88', padding: '14px 32px', fontSize: '13px', fontWeight: 700,
    letterSpacing: '3px', cursor: 'pointer', fontFamily: theme.fonts.mono,
    borderRadius: '4px', transition: 'all 0.15s',
  },

  // Card tile — used for direct buys and pack options (matches hand-card visual)
  cardTile: {
    width: '90px', height: '120px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    gap: '3px', position: 'relative',
    background: 'rgba(12, 16, 24, 0.92)', border: '1px solid rgba(30, 42, 58, 0.6)',
    borderRadius: '6px', cursor: 'pointer',
    fontFamily: theme.fonts.mono, transition: 'all 0.15s',
  },
  cardTileCost: {
    position: 'absolute', top: '6px', left: '8px',
    fontSize: '14px', fontWeight: 700, color: theme.colors.accentYellow,
  },
  cardTileName: {
    fontSize: '11px', fontWeight: 600, color: theme.colors.textPrimary,
    letterSpacing: '0.5px', textAlign: 'center', padding: '0 6px',
  },
  cardTileCat: {
    fontSize: '8px', letterSpacing: '1.5px', color: theme.colors.textDim,
  },
  cardTileRange: {
    position: 'absolute', bottom: '6px', right: '6px',
    fontSize: '9px', fontWeight: 600, letterSpacing: '0.5px',
  },
  cardTilePrice: {
    position: 'absolute', bottom: '6px', left: '6px',
    fontSize: '8px', fontWeight: 600, color: '#44cc88', letterSpacing: '0.5px',
  },
};
